import React from 'react';
import type { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Page from '@atlaskit/page';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';

import { fg } from '@confluence/feature-gating';
import { Link } from '@confluence/route-manager';
import { ADMIN_SITE_SPACE_PERMISSIONS } from '@confluence/named-routes';
import { PaginationProvider } from '@confluence/space-permissions/entry-points/Pagination';
import { SpaceRolesLozenge } from '@confluence/space-roles';
import { useIsProductAdmin } from '@confluence/current-user';
import { RestrictedPage } from '@confluence/no-permission';
import { FeedbackCollectorButton } from '@confluence/space-roles/entry-points/FeedbackCollector';

import { DefaultSettingsTabWithFlags } from './tabs/DefaultSettingsTab/DefaultSettingsTab';
import { IndividualSpacesTableComponentWithFlags } from './tabs/AllSpacesTab/IndividualSpacesComponent';
import { DefaultSettingsTabV2 } from './tabs/DefaultSettingsTabV2/DefaultSettingsTabV2';
import { SpaceRolesBanner } from './space-roles/onboarding/SpaceRolesBanner';
import { RolesManagementTab } from './tabs/RolesManagementTab/RolesManagementTab';

const i18n = defineMessages({
	spacePermissionsHeader: {
		id: 'default-space-permissions.space-permissions-header',
		defaultMessage: 'Space permissions',
		description: 'Header for the space permissions page',
	},
	allSpaceTabTitle: {
		id: 'default-space-permissions.all-space-tab-title',
		defaultMessage: 'All spaces',
		description: 'Title for the all spaces tab',
	},
	defaultsTabTitle: {
		id: 'default-space-permissions.defaults-tab-title',
		defaultMessage: 'Defaults for new spaces',
		description: 'Title for the defaults tab',
	},
	rolesManagementTabTitle: {
		id: 'default-space-permissions.roles-management-tab-title',
		defaultMessage: 'Roles',
		description: 'Title for the roles management tab',
	},
});

const headerStyle = xcss({
	marginBottom: 'space.250',
});

const headerAndFeedbackStyle = xcss({
	width: '100%',
	justifyContent: 'space-between',
	display: 'flex',
	flexDirection: 'row',
});

const pagePadding = xcss({
	padding: 'space.500',
});

const bannerStyle = xcss({
	paddingTop: 'space.200',
	paddingBottom: 'space.300',
});

const tabContentStyle = xcss({
	width: '100%',
	marginTop: 'space.300',
});

// Make sure to update the route if you update this enum
export enum ActiveTab {
	ALL_SPACES = 'allspaces',
	DEFAULTS = 'defaults',
	ROLES = 'roles',
}

export const DefaultSpacePermissionsPage: FC<{ activeTab: ActiveTab }> = ({ activeTab }) => {
	const { formatMessage } = useIntl();
	const { isProductAdmin, isAdminCheckLoading } = useIsProductAdmin();

	const DEFAULT_SPACE_PERMS_TABS = [
		{
			id: ActiveTab.ALL_SPACES,
			link: ADMIN_SITE_SPACE_PERMISSIONS.toUrl({
				tab: ActiveTab.ALL_SPACES,
			}),
			title: formatMessage(i18n.allSpaceTabTitle),
			content: (
				<PaginationProvider>
					<IndividualSpacesTableComponentWithFlags />
				</PaginationProvider>
			),
		},
		{
			id: ActiveTab.DEFAULTS,
			link: ADMIN_SITE_SPACE_PERMISSIONS.toUrl({
				tab: ActiveTab.DEFAULTS,
			}),
			title: (
				<Inline alignBlock="baseline" space="space.050">
					{formatMessage(i18n.defaultsTabTitle)}
					<SpaceRolesLozenge destination="default-roles-tab" />
				</Inline>
			),
			content: fg('cc_perms_exp_rbac_fe_milestone_2') ? (
				<DefaultSettingsTabV2 />
			) : (
				<PaginationProvider>
					<DefaultSettingsTabWithFlags />
				</PaginationProvider>
			),
		},
		...(fg('cc_perms_exp_rbac_fe_milestone_3')
			? [
					{
						id: ActiveTab.ROLES,
						link: ADMIN_SITE_SPACE_PERMISSIONS.toUrl({
							tab: ActiveTab.ROLES,
						}),
						title: formatMessage(i18n.rolesManagementTabTitle),
						content: <RolesManagementTab />,
					},
				]
			: []),
	];

	const selectedTabIndex: number =
		DEFAULT_SPACE_PERMS_TABS.findIndex((tab) => tab.id === activeTab) || 0;

	const isAllSpaceTab = activeTab === ActiveTab.ALL_SPACES;
	const isDefaultsTab = activeTab === ActiveTab.DEFAULTS;
	const isRolesTab = activeTab === ActiveTab.ROLES;
	// Note that the only reason we are looking at this is because the monolith
	// rendered page is still being presented in free editions.
	const isUpliftedPage = isDefaultsTab || isAllSpaceTab || isRolesTab;

	if (isAdminCheckLoading) return null;

	if (!isProductAdmin) return <RestrictedPage />;

	return (
		<Box xcss={isUpliftedPage ? pagePadding : null}>
			<Page>
				{isUpliftedPage && (
					<Box xcss={headerAndFeedbackStyle}>
						<Box xcss={headerStyle}>
							<Heading size="large" as="h1">
								{formatMessage(i18n.spacePermissionsHeader)}
							</Heading>
						</Box>
						{!isAllSpaceTab && <FeedbackCollectorButton source="Site Space Permissions" />}
					</Box>
				)}
				{fg('cc_perms_exp_rbac_fe_milestone_2') && (
					<Box xcss={bannerStyle}>
						<SpaceRolesBanner isDefaultsTab={isDefaultsTab} />
					</Box>
				)}
				<Tabs id="default-space-permissions" selected={selectedTabIndex}>
					<TabList>
						{DEFAULT_SPACE_PERMS_TABS.map(({ id, link, title }) => (
							<Tab key={id}>
								<Link href={link}>{title}</Link>
							</Tab>
						))}
					</TabList>
					{DEFAULT_SPACE_PERMS_TABS.map(({ id, content }) => (
						<TabPanel key={id}>
							<Box xcss={tabContentStyle}>{content}</Box>
						</TabPanel>
					))}
				</Tabs>
			</Page>
		</Box>
	);
};
